import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    NCoreUIKitLocalize,
    type IUploadFile,
    NCoreUIKitTheme,
    DateTimePicker,
    RadioButton,
    RadioGroup,
    FileUpload,
    Breadcrumb,
    Separator,
    Stepper,
    Avatar,
    Button,
    Alert,
    Field,
    Card,
    Item,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    Accessibility,
    ExternalLink,
    ShieldCheck,
    ArrowRight,
    ArrowLeft,
    Phone
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

const PhoneIcon = createKitIcon(Phone);

const BackIcon = createKitIcon(ArrowLeft);

const NextIcon = createKitIcon(ArrowRight);

const ExternalIcon = createKitIcon(ExternalLink);

const ShieldIcon = createKitIcon(ShieldCheck);

const AccessibilityIcon = createKitIcon(Accessibility);

const STEPS = [
    "identity",
    "address",
    "documents",
    "preview",
    "submit"
];

const ACTIVE_STEP = 2;

const REQUIRED_DOCUMENTS = [
    "report",
    "photo",
    "residence"
];

const HOURS = [
    "weekdays",
    "saturday",
    "sunday"
];

const DELIVERIES = [
    "cargo",
    "branch"
];

const KILOBYTE = 1024;

const MEGABYTE = KILOBYTE * KILOBYTE;

const MAX_FILE_SIZE = 5 * MEGABYTE;

const ACCEPTED_FILES = [
    ".pdf",
    ".jpg",
    ".png"
];

const INITIAL_FILES: Array<IUploadFile> = [
    {
        name: "saglik-kurulu-raporu.pdf",
        size: 1.8 * MEGABYTE,
        key: "report",
        status: "done"
    },
    {
        name: "vesikalik-fotograf.jpg",
        size: 420 * KILOBYTE,
        status: "done",
        key: "photo"
    },
    {
        name: "ikametgah-belgesi.pdf",
        size: 7.4 * MEGABYTE,
        key: "residence",
        status: "error"
    }
];

const SAVED_AT = new Date(2026, 10, 1, 14, 32);

const REPORT_EXPIRY = new Date(2028, 2, 14);

const stylesheet = StyleSheet.create({
    header: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        width: "100%"
    },
    footer: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    logo: {
        justifyContent: "center",
        alignItems: "center"
    },
    link: {
        alignSelf: "flex-start"
    },
    row: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    column: {
        alignSelf: "center",
        width: "100%"
    },
    page: {
        minHeight: "100%",
        width: "100%"
    },
    picker: {
        maxWidth: "100%"
    },
    cell: {
        flexShrink: 1,
        minWidth: 0
    },
    main: {
        minWidth: 0,
        flex: 1,
        flexShrink: 1
    },
    side: {
        flexGrow: 0
    }
});

const handlePress = () => undefined;

const Form01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        files,
        setFiles
    ] = useState<Array<IUploadFile>>(INITIAL_FILES);

    const [
        delivery,
        setDelivery
    ] = useState("cargo");

    const isWide = width >= shapeConfigs.wideBreakpoint;

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const sizeFormat = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 1
    });

    const savedFormat = new Intl.DateTimeFormat(activeLocale, {
        minute: "2-digit",
        hour: "2-digit",
        day: "numeric",
        month: "short",
        year: "numeric"
    });

    const formatSize = (size?: number) => {
        if(size === undefined) {
            return "";
        }

        return size >= MEGABYTE ? `${sizeFormat.format(size / MEGABYTE)} MB` : `${sizeFormat.format(size / KILOBYTE)} KB`;
    };

    const stepEntries = STEPS.map((key) => ({
        title: t(`blocks.form01.step_${key}`),
        key
    }));

    const displayFiles = files.map((file) => file.status === "error" ? {
        ...file,
        errorText: t("blocks.form01.tooLarge", [
            formatSize(file.size)
        ])
    } : file);

    const handleFilesChange = (next: Array<IUploadFile>) => setFiles(next.map((file) => file.status === "queued" ? {
        ...file,
        status: "done"
    } : file));

    const handleFileRemove = (key: string) => setFiles(files.filter((file) => file.key !== key));

    const handleDeliveryChange = (value: string) => setDelivery(value);

    const renderRequired = (key: string) => <View
        style={[
            stylesheet.row,
            {
                gap: spaces.spacingSm
            }
        ]}
        key={key}
    >
        <Text
            variant="bodyMediumSize"
            color="high"
        >
            {`• ${t(`blocks.form01.required_${key}`)}`}
        </Text>
    </View>;

    const renderHours = (key: string) => <Item
        size="xSmall"
        key={key}
    >
        <Item.Content>
            <Item.Title>
                {t(`blocks.form01.day_${key}`)}
            </Item.Title>
        </Item.Content>
        <Item.Actions>
            <Text
                variant="bodySmallSize"
                color="high"
            >
                {t(`blocks.form01.hours_${key}`)}
            </Text>
        </Item.Actions>
    </Item>;

    const renderDelivery = (key: string) => <RadioButton
        subTitle={t(`blocks.form01.delivery_${key}_sub`)}
        title={t(`blocks.form01.delivery_${key}`)}
        spreadBehaviour="stretch"
        variant="card"
        value={key}
        key={key}
    />;

    return <ScrollView
        contentContainerStyle={stylesheet.page}
    >
        <View
            style={[
                stylesheet.header,
                {
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingSm,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <View
                style={[
                    stylesheet.row,
                    stylesheet.cell,
                    {
                        gap: spaces.spacingSm,
                        flexWrap: "nowrap"
                    }
                ]}
            >
                <Avatar
                    icon={AccessibilityIcon}
                    type="neutral"
                    size="medium"
                />
                <View
                    style={stylesheet.cell}
                >
                    <Text
                        variant="titleMediumSize"
                        color="high"
                    >
                        {t("blocks.form01.institution")}
                    </Text>
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.form01.eServices")}
                    </Text>
                </View>
            </View>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                {
                    isCompact ?
                        null
                    :
                        <Button
                            title={t("blocks.form01.phone")}
                            onPress={handlePress}
                            icon={PhoneIcon}
                            variant="link"
                            type="neutral"
                        />
                }
                <View
                    style={[
                        stylesheet.row,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Avatar
                        title={t("blocks.form01.user")}
                        size="xSmall"
                    />
                    {
                        isCompact ?
                            null
                        :
                            <Text
                                variant="bodyMediumSize"
                                color="high"
                            >
                                {t("blocks.form01.user")}
                            </Text>
                    }
                </View>
            </View>
        </View>
        <Separator
            length="100%"
        />
        <View
            style={[
                stylesheet.column,
                {
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingXl
                }
            ]}
        >
            <Breadcrumb>
                <Breadcrumb.List>
                    <Breadcrumb.Item>
                        <Breadcrumb.Link
                            title={t("blocks.form01.crumb_home")}
                            onPress={handlePress}
                        />
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <Breadcrumb.Separator/>
                        <Breadcrumb.Link
                            title={t("blocks.form01.eServices")}
                            onPress={handlePress}
                        />
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <Breadcrumb.Separator/>
                        <Breadcrumb.Page
                            title={t("blocks.form01.title")}
                        />
                    </Breadcrumb.Item>
                </Breadcrumb.List>
            </Breadcrumb>
            <View
                style={{
                    gap: spaces.spacingSm
                }}
            >
                <Text
                    variant="headlineMediumSize"
                    accessibilityRole="header"
                    color="high"
                >
                    {t("blocks.form01.title")}
                </Text>
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t("blocks.form01.lead2")}
                </Text>
            </View>
            <Stepper
                accessibilityLabel={t("blocks.form01.stepsLabel")}
                activeIndex={ACTIVE_STEP}
                steps={stepEntries}
            />
            <View
                style={{
                    alignItems: isWide ? "flex-start" : undefined,
                    flexDirection: isWide ? "row" : "column",
                    gap: spaces.spacingXl
                }}
            >
                <View
                    style={[
                        stylesheet.main,
                        {
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    <View
                        style={{
                            gap: spaces.spacingMd
                        }}
                    >
                        <Text
                            variant="headlineSmallSize"
                            accessibilityRole="header"
                            color="high"
                        >
                            {t("blocks.form01.documents")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                            color="high"
                        >
                            {t("blocks.form01.documentsLead")}
                        </Text>
                        <View>
                            {REQUIRED_DOCUMENTS.map(renderRequired)}
                        </View>
                        <FileUpload
                            description={t("blocks.form01.uploadDescription")}
                            title={t("blocks.form01.uploadTitle")}
                            onChange={handleFilesChange}
                            onRemove={handleFileRemove}
                            accept={ACCEPTED_FILES}
                            maxSize={MAX_FILE_SIZE}
                            files={displayFiles}
                            isMultiple={true}
                        />
                        <Alert
                            description={t("blocks.form01.privacyDescription")}
                            title={t("blocks.form01.privacyTitle")}
                            icon={ShieldIcon}
                            type="info"
                        />
                    </View>
                    <Separator
                        length="100%"
                    />
                    <Field.Group>
                        <Text
                            variant="headlineSmallSize"
                            accessibilityRole="header"
                            color="high"
                        >
                            {t("blocks.form01.delivery")}
                        </Text>
                        <DateTimePicker
                            hintText={t("blocks.form01.expiryHint")}
                            title={t("blocks.form01.expiry")}
                            initialDate={REPORT_EXPIRY}
                            spreadBehaviour="stretch"
                            style={stylesheet.picker}
                            titleFormat="DD.MM.YYYY"
                            isRequired={true}
                            pickerType="date"
                        />
                        <Field.Set>
                            <Field.Legend>
                                {t("blocks.form01.deliveryQuestion")}
                            </Field.Legend>
                            <RadioGroup
                                aria-label={t("blocks.form01.deliveryQuestion")}
                                onValueChange={handleDeliveryChange}
                                value={delivery}
                            >
                                {DELIVERIES.map(renderDelivery)}
                            </RadioGroup>
                        </Field.Set>
                    </Field.Group>
                    <Separator
                        length="100%"
                    />
                    <View
                        style={[
                            stylesheet.footer,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Button
                            title={t("blocks.form01.back")}
                            onPress={handlePress}
                            icon={BackIcon}
                            variant="ghost"
                            type="neutral"
                        />
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <Button
                                title={t("blocks.form01.saveDraft")}
                                onPress={handlePress}
                                variant="outline"
                                type="neutral"
                            />
                            <Button
                                title={t("blocks.form01.next")}
                                onPress={handlePress}
                                variant="filled"
                                icon={NextIcon}
                                type="primary"
                            />
                        </View>
                    </View>
                </View>
                <View
                    style={[
                        stylesheet.side,
                        {
                            width: isWide ? shapeConfigs.cardWidth : "100%",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Card
                        variant="outline"
                    >
                        <Card.Header>
                            <Card.Title>
                                {t("blocks.form01.helpTitle")}
                            </Card.Title>
                            <Card.Description>
                                {t("blocks.form01.helpText")}
                            </Card.Description>
                        </Card.Header>
                        <Card.Content>
                            <View
                                style={{
                                    gap: spaces.spacingMd
                                }}
                            >
                                <View
                                    style={{
                                        gap: spaces.spacingXs
                                    }}
                                >
                                    <Text
                                        variant="headlineSmallSize"
                                        color="high"
                                    >
                                        {t("blocks.form01.phone")}
                                    </Text>
                                    <Text
                                        variant="bodySmallSize"
                                        color="mid"
                                    >
                                        {t("blocks.form01.phoneNote")}
                                    </Text>
                                </View>
                                <Separator
                                    length="100%"
                                />
                                <Item.Group>
                                    {HOURS.map(renderHours)}
                                </Item.Group>
                                <Separator
                                    length="100%"
                                />
                                <Button
                                    title={t("blocks.form01.faq")}
                                    style={stylesheet.link}
                                    onPress={handlePress}
                                    icon={ExternalIcon}
                                    variant="link"
                                    type="neutral"
                                    size="small"
                                />
                            </View>
                        </Card.Content>
                    </Card>
                    <Card
                        variant="filled"
                    >
                        <Card.Content>
                            <View
                                style={{
                                    gap: spaces.spacingXs
                                }}
                            >
                                <Text
                                    variant="labelSmallSize"
                                    color="mid"
                                >
                                    {t("blocks.form01.applicationNumber")}
                                </Text>
                                <Text
                                    variant="monoMediumSize"
                                    color="high"
                                >
                                    {t("blocks.form01.applicationId")}
                                </Text>
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {t("blocks.form01.autosaved", [
                                        savedFormat.format(SAVED_AT)
                                    ])}
                                </Text>
                            </View>
                        </Card.Content>
                    </Card>
                </View>
            </View>
        </View>
    </ScrollView>;
};
export default Form01;
